import React from "react";
import { Card, Table, Tag, Typography, Empty } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { dayStatuses } from "../../core/utils/weekPlanUtils";

const { Title, Text } = Typography;

// Colours mirror statusMetaMap in workStatusApp/WorkStatusApplication.jsx so a
// request's status reads the same here as on the applications page.
const STATUS_COLORS = {
  0: "default", // Pending
  1: "blue", // Requested
  2: "green", // Approved
  3: "red", // Rejected
  4: "orange", // Cancelled
};

const fmtMinutes = (min) => {
  const sign = min >= 0 ? "+" : "-";
  const abs = Math.abs(min);
  return `${sign}${Math.floor(abs / 60)}h ${abs % 60}min`;
};

const fmtDate = (d) => (d ? dayjs(d).format("DD.MM.YYYY") : "");

/**
 * Printable report body. Pure presentation — it renders whatever `buildReport`
 * produced. `ReportTab` wraps this in a ref and hands the DOM node to the PDF
 * exporter.
 */
const ReportView = React.forwardRef(({ report }, ref) => {
  const { t } = useTranslation();

  const typeLabel = (dayStatus) => {
    const name = dayStatuses[dayStatus];
    return name ? t(`reasons.${name.toLowerCase()}`) : String(dayStatus);
  };
  const statusLabel = (requestStatus) => t(`report.status.${requestStatus}`);

  const weekColumns = [
    { title: t("year"), dataIndex: "year", key: "year", align: "center" },
    {
      title: t("calendarWeek"),
      dataIndex: "weekCalendarNumber",
      key: "week",
      align: "center",
      render: (w) => `${t("week")} ${w}`,
    },
    {
      title: t("actualTime"),
      key: "actual",
      align: "center",
      render: (_, r) => `${r.actualWorkTimeHours}h ${r.actualWorkTimeMinutes}min`,
    },
    {
      title: t("plannedTime"),
      key: "planned",
      align: "center",
      render: (_, r) => `${r.shouldWorkTimeHours}h ${r.shouldWorkTimeMinutes}min`,
    },
    {
      title: t("report.diff"),
      key: "diff",
      align: "center",
      render: (_, r) =>
        fmtMinutes(
          r.actualWorkTimeHours * 60 +
            r.actualWorkTimeMinutes -
            (r.shouldWorkTimeHours * 60 + r.shouldWorkTimeMinutes)
        ),
    },
    {
      title: t("report.usedVacation"),
      dataIndex: "actualHolidayDays",
      key: "usedVacation",
      align: "center",
    },
  ];

  const appColumns = [
    {
      title: t("report.applicationType"),
      dataIndex: "dayStatus",
      key: "type",
      render: (v) => typeLabel(v),
    },
    {
      title: t("report.applicationStatus"),
      dataIndex: "requestStatus",
      key: "status",
      render: (v) => <Tag color={STATUS_COLORS[v] || "default"}>{statusLabel(v)}</Tag>,
    },
    {
      title: t("report.from"),
      dataIndex: "dateFrom",
      key: "from",
      render: (v) => fmtDate(v),
    },
    { title: t("report.to"), dataIndex: "dateTo", key: "to", render: (v) => fmtDate(v) },
    {
      title: t("report.days"),
      dataIndex: "holidayDays",
      key: "days",
      align: "center",
      render: (v) => (v == null ? "—" : v),
    },
    { title: t("report.comment"), dataIndex: "comment", key: "comment", render: (v) => v || "" },
  ];

  // Week-plan notices (per-day comments the employee wrote in their week plan).
  const noticeColumns = [
    {
      title: t("calendarWeek"),
      dataIndex: "week",
      key: "week",
      align: "center",
      render: (w) => `${t("week")} ${w}`,
    },
    { title: t("report.date"), dataIndex: "date", key: "date", render: (v) => fmtDate(v) },
    {
      title: t("report.day"),
      dataIndex: "day",
      key: "day",
      render: (v) => t(`days.${String(v).toLowerCase()}`),
    },
    { title: t("report.comment"), dataIndex: "comment", key: "comment" },
  ];

  return (
    <div
      ref={ref}
      data-testid="report.view"
      data-report-root=""
      style={{ background: "#fff", padding: 16 }}
    >
      <div data-report-header="">
        <Title level={4}>{t("report.title")}</Title>
        <Text type="secondary">
          {t("report.period")}: {fmtDate(report.from)} – {fmtDate(report.to)}
        </Text>
      </div>

      {report.employees.length === 0 && (
        <Empty
          data-testid="report.empty"
          description={t("report.empty")}
          style={{ marginTop: 24 }}
        />
      )}

      {report.employees.map(({ employee, stats, weeks, applications, notices = [] }) => (
        <Card
          key={employee.email || employee.userId}
          data-testid={`report.employee.${employee.email}`}
          data-report-employee=""
          style={{ marginTop: 16 }}
          title={
            <div>
              <div>{employee.userName}</div>
              <Text type="secondary" style={{ fontWeight: "normal" }}>
                {employee.email}
              </Text>
            </div>
          }
        >
          {/* Headline summary figures (same maths as the Summary page). */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 24, marginBottom: 16 }}>
            <Statistic label={t("report.netBalance")} value={fmtMinutes(stats.netMinutes)} />
            <Statistic
              label={t("report.paidOvertime")}
              value={fmtMinutes(stats.paidOvertimeMinutes)}
            />
            <Statistic
              label={t("report.usedVacationTotal")}
              value={`${stats.usedVacationDays} ${t("days_name")}`}
            />
            <Statistic
              label={t("report.remainingVacation")}
              value={`${stats.remainingVacation} ${t("days_name")}`}
            />
          </div>

          <Title level={5}>{t("report.weeklyBreakdown")}</Title>
          <Table
            size="small"
            pagination={false}
            dataSource={weeks}
            columns={weekColumns}
            rowKey={(r) => `${r.year}-${r.weekCalendarNumber}`}
            locale={{ emptyText: t("report.noWeeks") }}
          />

          <Title level={5} style={{ marginTop: 16 }}>
            {t("report.applications")}
          </Title>
          <Table
            size="small"
            pagination={false}
            dataSource={applications}
            columns={appColumns}
            rowKey={(r) => r.id}
            locale={{ emptyText: t("report.noApplications") }}
          />

          <Title level={5} style={{ marginTop: 16 }}>
            {t("report.notices")}
          </Title>
          <Table
            size="small"
            pagination={false}
            dataSource={notices}
            columns={noticeColumns}
            rowKey={(r) => `${r.year}-${r.week}-${r.day}`}
            locale={{ emptyText: t("report.noNotices") }}
          />
        </Card>
      ))}
    </div>
  );
});

const Statistic = ({ label, value }) => (
  <div>
    <div>
      <Text type="secondary">{label}</Text>
    </div>
    <div>
      <Text strong>{value}</Text>
    </div>
  </div>
);

ReportView.displayName = "ReportView";

export default ReportView;
